import React from 'react';
import TopBar from '@/components/atelier/TopBar';
import Nav from '@/components/atelier/Nav';
import Footer from '@/components/atelier/Footer';
import EyebrowPill from '@/components/atelier/EyebrowPill';
import Button from '@/components/atelier/Button';
import FAQAccordion from '@/components/atelier/FAQAccordion';
import FinalCTACard from '@/components/atelier/FinalCTACard';
import Block from '@/components/prosopologia/Block';
import CardGrid from '@/components/prosopologia/CardGrid';
import ComparisonTable from '@/components/prosopologia/ComparisonTable';
import { READING, APPLICATIONS, STEPS, FAQS, SCHEMA, BOOKING_URL } from '@/components/prosopologia/prosopologiaData';

export default function ProsopologiaVisagisme() {
  return (
    <div className="bg-cream-bg text-warm-black min-h-screen">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SCHEMA) }} />
      <TopBar />
      <Nav />
      <main id="main-content">
        <nav aria-label="Breadcrumb" className="px-[6vw] py-4 bg-cream-soft/40 border-b border-brown/10">
          <div className="max-w-container mx-auto text-tiny text-muted flex gap-2 flex-wrap">
            <a href="/">Home</a><span>/</span><a href="/services">Υπηρεσίες</a><span>/</span>
            <span className="text-warm-black">Προσωπολογία (Visagisme)</span>
          </div>
        </nav>

        <section className="px-[6vw] pt-[90px] pb-[60px]">
          <div className="max-w-editorial mx-auto text-center">
            <EyebrowPill className="mb-7">Προσωπολογία (Visagisme) στο Χαλάνδρι</EyebrowPill>
            <h1 className="font-serif text-warm-black mb-7">Προσωπολογία (Visagisme): η εικόνα που <em className="text-gold">σας εκφράζει</em></h1>
            <p className="text-body-l text-muted max-w-[640px] mx-auto mb-8">Το κούρεμα και το χρώμα δεν είναι μόνο θέμα αναλογιών. Είναι ο τρόπος που η εικόνα σας μιλά για το ποιοι είστε. Στο Ikonomakis Hair Atelier, η προσωπολογία συναντά την προσωπομετρία του Face Metrics Pro.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="primary" href={BOOKING_URL} target="_blank" rel="noopener noreferrer">Κράτηση Online →</Button>
              <Button variant="ghost" href="/services/face-metrics-pro">Face Metrics Pro</Button>
            </div>
          </div>
        </section>

        <Block eyebrow="Ορισμός" title="Τι είναι η προσωπολογία">
          <div className="max-w-reading space-y-5 text-body-l text-muted">
            <p>Η προσωπολογία (visagisme) είναι η τέχνη του σχεδιασμού μιας προσωπικής εικόνας που εκφράζει ποιος είναι ο άνθρωπος. Διαβάζει το πρόσωπο σαν γλώσσα: τις γραμμές και τα σχήματα των χαρακτηριστικών, την έκφραση, την ιδιοσυγκρασία που μεταδίδουν και το μήνυμα που θέλετε να περνά η εικόνα σας.</p>
            <p>Με βάση αυτή την ανάγνωση επιλέγονται κούρεμα, χρώμα, αντίθεση, γραμμές και όγκοι, ώστε η εξωτερική εικόνα να έρχεται σε αρμονία με την εσωτερική ταυτότητα, τον τρόπο ζωής και τους στόχους σας. Είναι ανθρώπινη, ερμηνευτική διαδικασία που βασίζεται στον διάλογο — όχι μόνο μέτρηση.</p>
          </div>
        </Block>

        <Block tinted eyebrow="Ανάγνωση" title="Πώς διαβάζουμε το πρόσωπο"><CardGrid items={READING} /></Block>

        <Block eyebrow="Σύγκριση" title="Προσωπολογία vs προσωπομετρία" intro="Δύο διαφορετικά, συμπληρωματικά εργαλεία. Η προσωπομετρία δίνει τις μετρήσεις — τι ταιριάζει στη γεωμετρία. Η προσωπολογία δίνει το νόημα — τι σας εκφράζει. Το Face Metrics Pro μετρά· ο stylist ερμηνεύει και σχεδιάζει το look μαζί σας.">
          <ComparisonTable />
        </Block>

        <Block tinted eyebrow="Εφαρμογή" title="Πώς εφαρμόζεται στο κούρεμα και στο χρώμα"><CardGrid items={APPLICATIONS} /></Block>

        <Block eyebrow="Consultation" title="Πώς γίνεται η consultation στο atelier" intro="Διάλογος, μετρήσεις, πρόταση — σε αυτή τη σειρά.">
          <CardGrid items={STEPS} numbered />
          <p className="mt-8 text-body text-muted">Δείτε επίσης: <a href="/hair-consultation-athens" className="text-gold underline underline-offset-2">Hair Consultation Athens</a> · <a href="/services/haircuts" className="text-gold underline underline-offset-2">Κουρέματα</a> · <a href="/services/face-metrics-pro" className="text-gold underline underline-offset-2">Face Metrics Pro</a></p>
        </Block>

        <Block tinted eyebrow="Για όλους" title="Για γυναίκες και άνδρες">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-body text-muted">
            <p><strong className="text-warm-black">Γυναίκες:</strong> από ένα απαλό lob με curtain bangs μέχρι ένα αρχιτεκτονικό bob, η προσωπολογία καθορίζει αν η εικόνα θα είναι πιο ρομαντική, πιο δυναμική ή πιο κομψή — και το χρώμα ακολουθεί με την ανάλογη αντίθεση.</p>
            <p><strong className="text-warm-black">Άνδρες:</strong> fade, textured crop ή κλασικό side part, μήκος και σχήμα γενιών — η ίδια λογική εφαρμόζεται για μια εικόνα πιο επαγγελματική, πιο χαλαρή ή πιο δημιουργική.</p>
          </div>
        </Block>

        <Block eyebrow="FAQ" title="Συχνές ερωτήσεις για την προσωπολογία"><FAQAccordion items={FAQS} /></Block>

        <section className="px-[6vw] py-[100px]">
          <FinalCTACard title="Σχεδιάστε την εικόνα" titleEm="που σας εκφράζει." description="Κλείστε ραντεβού online — η consultation προσωπολογίας ξεκινά πριν από κάθε κούρεμα." ctaText="Κράτηση Online →" ctaHref={BOOKING_URL} ctaTarget="_blank" ctaRel="noopener noreferrer" />
        </section>
      </main>
      <Footer />
    </div>
  );
}